import { ComingSoonForLiveTvTray } from '../../../domain/coming-soon-for-live-tv-tray';
import {
  CreateUpcomingLiveTvPageItemRequestApiModel,
  UpcomingLiveTvPageItemApiModel,
  UpdateUpcomingLiveTvPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  displayServiceTypeIconMapperDomainToApi,
  layoutMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  commonMapper,
  displayServiceTypeIconMapperApiToDomain,
  layoutMapperApiToDomain,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { ComingSoonForLiveTvChannelSubtype, ComingSoonForLiveTvTrayViewModel } from './coming-soon-for-live-tv-tray.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  displayServiceTypeIconMapper,
  layoutMapper,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'ComingSoonForLiveTvTray'

/**
 * DOMAIN -> API
 */

export const mapComingSoonForLiveTvTrayDomainModelToApiModel = (
  item: ComingSoonForLiveTvTray,
  pageVersionId?: string,
  tenantId?: string
): CreateUpcomingLiveTvPageItemRequestApiModel & UpdateUpcomingLiveTvPageItemRequestApiModel => {
  const commonProperties = mapperPipe(
    item,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    displayServiceTypeIconMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    layoutMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    items: item.items.map((itemItem) => ({
      id: itemItem.id,
      order: itemItem.order,
      programmingTime: itemItem.programStartTime ? new Date(itemItem.programStartTime).getTime() : undefined,
      programmingEndTime: itemItem.programEndTime ? new Date(itemItem.programEndTime).getTime() : undefined,
      channelId: itemItem.channel.id,
      programId: itemItem.programId,
      instanceId: itemItem.instanceId,
    })),
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapComingSoonForLiveTVTrayApiModelToDomainModel = (pageItem: UpcomingLiveTvPageItemApiModel): ComingSoonForLiveTvTray => {
  return remainderMapper(
    mapperPipe(
      pageItem,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      displayServiceTypeIconMapperApiToDomain,
      layoutMapperApiToDomain
    ),
    () => ({
      type: 'ComingSoonForLiveTvTray',
      items: pageItem.items
        .sort((s) => s.order)
        .map((item) => ({
          id: item.id,
          order: item.order,
          programStartTime: item.programmingTime ? new Date(item.programmingTime).getTime() : undefined,
          programEndTime: item.programmingEndTime ? new Date(item.programmingEndTime).getTime() : undefined,
          programId: item.programId,
          instanceId: item.instanceId,
          channel: {
            id: item.channel.id,
            channelID: item.channel.channelId,
            channelName: item.channel.channelName,
            channelNumber: item.channel.channelNumber,
          },
        })),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapComingSoonForLiveTvTrayDomainModelToViewModel = (pageItem: ComingSoonForLiveTvTray): ComingSoonForLiveTvTrayViewModel => {
  const commonProperties = mapperPipe(
    pageItem,
    commonMapper,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel,
    displayServiceTypeIconMapper,
    layoutMapper
  );
  const subtype = pageItem?.items[0].programStartTime
    ? ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime
    : ComingSoonForLiveTvChannelSubtype.ComingNext;
  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.ComingSoonForLiveTvTray,
    subtype,
    channels: pageItem.items
      .map((item, index) => ({
        id: item.id,
        index: item.order,
        name: undefined,
        ...(subtype === ComingSoonForLiveTvChannelSubtype.ComingNext ? { channel: item.channel } : null),
        ...(subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime
          ? {
              programSelector: {
                channel: item.channel,
                programStartTime: item.programStartTime,
                programEndTime: item.programEndTime,
                programId: item.programId,
                instanceId: item.instanceId,
              },
            }
          : null),
      }))
      .sort((a, b) => a.index - b.index),
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapComingSoonForLiveTvTrayViewModelToDomainModel = (vm: ComingSoonForLiveTvTrayViewModel): ComingSoonForLiveTvTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapper,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper,
    layoutMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'ComingSoonForLiveTvTray',
    items: vm.channels.map((item, index) => ({
      id: item.id,
      order: index,
      programStartTime:
        vm.subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime && item.programSelector.programStartTime
          ? item.programSelector.programStartTime
          : undefined,
      programEndTime:
        vm.subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime && item.programSelector.programEndTime
          ? item.programSelector.programEndTime
          : undefined,
      channel: vm.subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime ? item.programSelector.channel : item.channel,
      programId:
        vm.subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime && item.programSelector.programId
          ? item.programSelector.programId
          : undefined,
      instanceId:
        vm.subtype === ComingSoonForLiveTvChannelSubtype.UpcomingAtSpecificTime && item.programSelector.instanceId
          ? item.programSelector.instanceId
          : undefined,
    })),
  }));
};
